import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { LeadQuoteForm } from "@/components/LeadQuoteForm";
import { SITE } from "@/content/site";

const CONTACT_CARDS = [
  {
    icon: Phone,
    label: "Call or Text",
    value: SITE.phone,
    href: SITE.phoneHref,
    testid: "contact-phone-link",
  },
  {
    icon: Mail,
    label: "Email",
    value: SITE.email,
    href: SITE.emailHref,
    testid: "contact-email-link",
  },
  {
    icon: MapPin,
    label: "Service Area",
    value: "All of Pinellas County, FL",
    href: "/service-areas/",
    testid: "contact-area-link",
    internal: true,
  },
];

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Request Pool Service & Get a Quote | Pinellas County FL | Point of Pines Pools"
        description="Request a quote for weekly pool service, equipment repair, or a new pool startup anywhere in Pinellas County FL. Call (727) 744-5908 or send the form — we respond fast."
        path="/contact/"
      />
      <PageHero
        overline="Contact — Request a Quote"
        title="Let's Get Your Pool"
        accent="On the Schedule"
        description="Tell us about your pool and what you need. We review every request personally and follow up with clear next steps — usually the same day."
      />

      <section data-testid="contact-section" className="py-16 sm:py-24 lg:py-32 bg-shell">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32 space-y-5">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-[1.15] text-navy">
                Talk to a local pool pro
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                Fastest response? Call or send the form. Either way, you&apos;re talking to the
                people who will actually service your pool.
              </p>
            </Reveal>
            {CONTACT_CARDS.map((c, i) => (
              <Reveal key={c.label} delay={0.08 + i * 0.06}>
                <a
                  href={c.href}
                  data-testid={c.testid}
                  className="group flex items-center gap-4 bg-white rounded-2xl border border-slate-200/80 p-5 transition-all duration-300 hover:border-aqua hover:shadow-lg hover:shadow-cyan-500/10 hover:-translate-y-0.5"
                >
                  <span className="w-12 h-12 rounded-xl bg-mist flex items-center justify-center shrink-0 transition-colors duration-300 group-hover:bg-navy">
                    <c.icon className="w-5 h-5 text-pool transition-colors duration-300 group-hover:text-sky2" />
                  </span>
                  <span>
                    <span className="block text-xs font-mono uppercase tracking-[0.18em] text-slate-400">{c.label}</span>
                    <span className="block font-display font-bold text-navy mt-1">{c.value}</span>
                  </span>
                </a>
              </Reveal>
            ))}
            <Reveal delay={0.28}>
              <div className="flex items-start gap-4 bg-mist rounded-2xl p-5">
                <Clock className="w-5 h-5 text-pool shrink-0 mt-0.5" />
                <p className="text-sm text-slate-600 leading-relaxed">
                  Weekly routes run across Pinellas County on consistent service days. Request a
                  quote and we&apos;ll confirm availability for your address.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.12}>
              <LeadQuoteForm />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
